import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Users, UserCheck, UserX, Baby, Home, Hotel, CreditCard, Clock, TrendingUp } from 'lucide-react';
import { AdminLayout } from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { getDashboard, getAdminMe } from '@/lib/api';

const StatCard = ({ icon: Icon, title, value, subtitle, color = 'terracotta' }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="bg-white rounded-xl border border-gray-100 p-6 hover:shadow-md transition-shadow"
  >
    <div className="flex items-start justify-between">
      <div>
        <p className="text-sm text-gray-500 mb-1">{title}</p>
        <p className="text-3xl font-bold text-gray-900">{value}</p>
        {subtitle && <p className="text-sm text-gray-400 mt-1">{subtitle}</p>}
      </div>
      <div className={`p-3 rounded-lg bg-${color}/10`}>
        <Icon className={`w-6 h-6 text-${color}`} style={{ color: color === 'terracotta' ? '#D4A373' : color === 'sage' ? '#5D6D58' : color }} />
      </div>
    </div>
  </motion.div>
);

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        await getAdminMe(); // Check auth
        const response = await getDashboard();
        setStats(response.data);
      } catch (error) {
        if (error.response?.status === 401) {
          navigate('/admin');
        }
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [navigate]);

  if (loading) {
    return (
      <AdminLayout title="Tableau de bord">
        <div className="flex items-center justify-center h-64">
          <div className="animate-pulse text-gray-500">Chargement...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Tableau de bord">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          icon={Users}
          title="Confirmations"
          value={stats?.total_rsvps || 0}
          subtitle="Réponses reçues"
        />
        <StatCard
          icon={UserCheck}
          title="Invités confirmés"
          value={stats?.confirmed_guests || 0}
          subtitle={`${stats?.declined_guests || 0} absent(s)`}
          color="sage"
        />
        <StatCard
          icon={TrendingUp}
          title="Total personnes"
          value={stats?.total_people || 0}
          subtitle={`${stats?.total_adults || 0} adultes, ${stats?.total_children || 0} enfants`}
        />
        <StatCard
          icon={CreditCard}
          title="Montant collecté"
          value={`${stats?.total_collected || 0}€`}
          subtitle={`${stats?.pending_amount || 0}€ en attente`}
          color="#16a34a"
        />
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Home className="w-5 h-5 text-terracotta" />
              Hébergement Domaine
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-gray-900">{stats?.domaine_accommodations || 0}</p>
            <p className="text-sm text-gray-500">demandes</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Hotel className="w-5 h-5 text-terracotta" />
              Hébergement Hôtel
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-gray-900">{stats?.hotel_accommodations || 0}</p>
            <p className="text-sm text-gray-500">demandes</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-500" />
              Paiements en attente
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-gray-900">{stats?.payments_pending || 0}</p>
            <p className="text-sm text-gray-500">à valider</p>
          </CardContent>
        </Card>
      </div>

      {/* Quick Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Résumé rapide</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <Users className="w-8 h-8 text-terracotta mx-auto mb-2" />
              <p className="text-2xl font-bold text-gray-900">{stats?.total_adults || 0}</p>
              <p className="text-sm text-gray-500">Adultes</p>
            </div>
            <div className="text-center p-4 bg-gray-50 rounded-lg">
              <Baby className="w-8 h-8 text-terracotta mx-auto mb-2" />
              <p className="text-2xl font-bold text-gray-900">{stats?.total_children || 0}</p>
              <p className="text-sm text-gray-500">Enfants</p>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <UserCheck className="w-8 h-8 text-green-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-gray-900">{stats?.confirmed_guests || 0}</p>
              <p className="text-sm text-gray-500">Présents</p>
            </div>
            <div className="text-center p-4 bg-red-50 rounded-lg">
              <UserX className="w-8 h-8 text-red-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-gray-900">{stats?.declined_guests || 0}</p>
              <p className="text-sm text-gray-500">Absents</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </AdminLayout>
  );
};

export default AdminDashboard;
